/* ==========================================================================
   Hero banner + products

   Keeps the product row in step with the banner slider. Every product carries
   a data-sg-group index pointing at the banner slide it belongs to.

   Desktop: only the active group is in the grid; the rest are display:none.
   Mobile:  every product stays in one scrollable row and the row scrolls to
            the active group's first product when the banner changes.

   The product row is a native scroll-snap container rather than a second
   Swiper — it has to be a CSS grid on desktop and a scroller on mobile, and a
   swiper element owns its children through a shadow-DOM slot, which makes that
   swap fragile.
   ========================================================================== */

(function () {
  'use strict';

  var DESKTOP_QUERY = '(min-width: 990px)';
  // While something is still in progress, re-check this often instead of
  // resuming, and give up waiting after this many tries.
  var RECHECK_MS = 500;
  var MAX_RECHECKS = 120;

  function HeroBannerProducts(root) {
    this.root = root;
    this.banner = root.querySelector('[data-sg-banner]');
    this.track = root.querySelector('[data-sg-track]');
    this.productsArea = root.querySelector('.sg-hero__products');
    this.prev = root.querySelector('[data-sg-prev]');
    this.next = root.querySelector('[data-sg-next]');
    this.products = Array.prototype.slice.call(root.querySelectorAll('[data-sg-group]'));
    this.mq = window.matchMedia(DESKTOP_QUERY);
    this.activeGroup = 0;

    this.pauseEnabled = root.getAttribute('data-sg-pause') === 'true';
    this.resumeDelay = parseInt(root.getAttribute('data-sg-resume-delay'), 10) || 4000;
    this.addDelay = parseInt(root.getAttribute('data-sg-add-delay'), 10) || 20000;
    this.pointerInside = false;
    this.resumeTimer = null;
    this.rechecks = 0;
    this.destroyed = false;
    this.fitFrame = null;
    this.fitConfirmFrame = null;
    this.lastFitHeight = null;
    this.lastFitProductsWidth = null;
    this.onBannerChange = this.onBannerChange.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onPrevClick = this.step.bind(this, -1);
    this.onNextClick = this.step.bind(this, 1);

    // Fit-to-viewport runs even for a banner-only section, so it sits above
    // the products bail-out below.
    this.fitEnabled = root.hasAttribute('data-sg-fit');
    if (this.fitEnabled) {
      this.measureFit = this.measureFit.bind(this);
      this.onFitResize = this.onFitResize.bind(this);
      window.addEventListener('resize', this.onFitResize);
      this.measureFit();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.onFitResize);
      } else {
        window.addEventListener('load', this.onFitResize, { once: true });
      }
    }

    if (!this.track || !this.products.length) return;

    this.groups = this.products.map(function (el) {
      return parseInt(el.getAttribute('data-sg-group'), 10) || 0;
    });

    if (this.banner) {
      // swiper-element re-dispatches swiper events on the host with an
      // eventsPrefix of "swiper".
      this.banner.addEventListener('swiperslidechange', this.onBannerChange);
      this.banner.addEventListener('swiperinit', this.onBannerChange);
    }

    if (this.prev) {
      this.prev.addEventListener('click', this.onPrevClick);
    }
    if (this.next) {
      this.next.addEventListener('click', this.onNextClick);
    }

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.pauseEnabled && this.productsArea) {
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onProductPointerDown = this.onProductPointerDown.bind(this);
      this.onProductClick = this.onProductClick.bind(this);
      this.tryResume = this.tryResume.bind(this);

      this.productsArea.addEventListener('pointerenter', this.onPointerEnter);
      this.productsArea.addEventListener('pointerleave', this.onPointerLeave);
      this.productsArea.addEventListener('pointerdown', this.onProductPointerDown, { passive: true });
      this.productsArea.addEventListener('click', this.onProductClick);
      this.productsArea.addEventListener('focusin', this.onPointerEnter);
      this.productsArea.addEventListener('focusout', this.onPointerLeave);
    }

    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this.onBreakpoint);
    } else if (typeof this.mq.addListener === 'function') {
      this.mq.addListener(this.onBreakpoint);
    }

    this.apply(false);
    this.updateArrows();
  }

  /* ---- Autoplay hold -----------------------------------------------------
     The banner rotating mid-purchase swaps the product set out from under the
     shopper, so any interaction with the products stops it, and it only starts
     again once the pointer has left, no quick-view dialog is open, and the
     grace period has passed. Only ever touches autoplay the merchant turned on.
     ---------------------------------------------------------------------- */

  HeroBannerProducts.prototype.canControlAutoplay = function () {
    return Boolean(
      this.pauseEnabled &&
        this.banner &&
        this.banner.hasAttribute('autoplay') &&
        this.banner.swiper &&
        this.banner.swiper.autoplay
    );
  };

  HeroBannerProducts.prototype.holdAutoplay = function () {
    if (!this.canControlAutoplay()) return;
    window.clearTimeout(this.resumeTimer);
    this.rechecks = 0;
    this.banner.swiper.autoplay.stop();
  };

  HeroBannerProducts.prototype.scheduleResume = function (delay) {
    if (!this.canControlAutoplay()) return;
    window.clearTimeout(this.resumeTimer);
    this.rechecks = 0;
    this.resumeTimer = window.setTimeout(this.tryResume, delay);
  };

  HeroBannerProducts.prototype.isBusy = function () {
    // A quick-view dialog belonging to this section is still open.
    return this.pointerInside || Boolean(this.root.querySelector('dialog[open]'));
  };

  HeroBannerProducts.prototype.tryResume = function () {
    if (!this.canControlAutoplay()) return;
    if (this.isBusy() && this.rechecks < MAX_RECHECKS) {
      this.rechecks += 1;
      this.resumeTimer = window.setTimeout(this.tryResume, RECHECK_MS);
      return;
    }
    this.rechecks = 0;
    this.banner.swiper.autoplay.start();
  };

  /* Hover is a mouse-only signal — a tap would set it and never clear it. */
  HeroBannerProducts.prototype.onPointerEnter = function (event) {
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    this.pointerInside = true;
    this.holdAutoplay();
  };

  HeroBannerProducts.prototype.onPointerLeave = function (event) {
    if (event && event.pointerType && event.pointerType !== 'mouse') return;
    this.pointerInside = false;
    this.scheduleResume(this.resumeDelay);
  };

  /* Covers taps and drags, which never fire pointerenter on touch. */
  HeroBannerProducts.prototype.onProductPointerDown = function () {
    this.holdAutoplay();
    this.scheduleResume(this.resumeDelay);
  };

  HeroBannerProducts.prototype.onProductClick = function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('[data-popup-open], .advanced-quick-view, .quick-add button, .quick-add a')) return;
    this.holdAutoplay();
    this.scheduleResume(this.addDelay);
  };

  /* ---- Fit to viewport ---------------------------------------------------
     With the section's fit setting on, the grid gets the height left between
     its top edge and the fold: viewport minus everything above the grid
     (header, announcement bar, section padding) minus the section's own
     bottom padding. Document offsets, not viewport ones, so the measurement
     holds wherever the page happens to be scrolled when it runs. The 460px
     floor matches the min-height in hero-banner-products.css — below it the
     section grows past the fold rather than crushing the cards.
     Desktop only; mobile keeps its natural height. */

  HeroBannerProducts.prototype.measureFit = function (secondPass) {
    if (!this.fitEnabled) return;
    if (!this.isDesktop()) {
      this.root.style.removeProperty('--sg-fit-height');
      this.root.style.removeProperty('--sg-fit-products-w');
      this.lastFitHeight = null;
      this.lastFitProductsWidth = null;
      return;
    }
    var grid = this.root.querySelector('.sg-hero__grid');
    if (!grid) return;
    var top = grid.getBoundingClientRect().top + window.scrollY;
    var rootStyles = getComputedStyle(this.root);
    var padBottom = parseFloat(rootStyles.paddingBottom) || 0;
    var height = window.innerHeight - top - padBottom;
    if (height < 460) height = 460;
    height = Math.round(height);
    if (height !== this.lastFitHeight) {
      this.root.style.setProperty('--sg-fit-height', height + 'px');
      this.lastFitHeight = height;
    }

    // Square tiles, collection-grid style: the products column takes exactly
    // the width that makes each media box 1:1 at the row height the fit
    // leaves, and the banner absorbs the leftover width instead of the tiles
    // stretching landscape.
    var rows = parseInt(rootStyles.getPropertyValue('--sg-rows'), 10) || 2;
    var cols = parseInt(rootStyles.getPropertyValue('--sg-cols'), 10) || 2;
    var gap = parseFloat(rootStyles.getPropertyValue('--sg-gap')) || 8;

    var infoH = 0;
    var infos = this.root.querySelectorAll(
      '.sg-hero__product:not(.sg-hero__product--hidden) .card > .card__content'
    );
    Array.prototype.forEach.call(infos, function (el) {
      if (el.offsetHeight > infoH) infoH = el.offsetHeight;
    });
    if (!infoH) infoH = 80;

    var rowH = (height - (rows - 1) * gap) / rows;
    var tileH = rowH - infoH;
    if (tileH < 170) tileH = 170;

    // The row spans the full content width like every other section: the
    // banner takes its capped share (--sg-fit-banner-max × height, from the
    // CSS column definition) and the tiles absorb ALL the remaining width.
    // Tiles may run up to 18% wider than tall — on white packshots cover
    // makes that invisible — before the row gives up and centers instead.
    var ratio = parseFloat(rootStyles.getPropertyValue('--sg-fit-banner-max')) || 1;
    var bannerW = Math.min(height * ratio, grid.clientWidth * 0.65);
    var productsW = grid.clientWidth - bannerW - gap;
    var colW = (productsW - (cols - 1) * gap) / cols;
    if (colW > tileH * 1.18) colW = tileH * 1.18;
    if (colW < tileH * 0.85) colW = tileH * 0.85;
    productsW = cols * colW + (cols - 1) * gap;
    productsW = Math.round(productsW);
    if (productsW !== this.lastFitProductsWidth) {
      this.root.style.setProperty('--sg-fit-products-w', productsW + 'px');
      this.lastFitProductsWidth = productsW;
    }

    // The new width can rewrap titles and change the info height, so run one
    // confirming pass on the next frame.
    if (!secondPass) {
      var self = this;
      window.cancelAnimationFrame(this.fitConfirmFrame);
      this.fitConfirmFrame = window.requestAnimationFrame(function () {
        self.fitConfirmFrame = null;
        self.measureFit(true);
      });
    }
  };

  HeroBannerProducts.prototype.onFitResize = function () {
    if (this.destroyed || this.fitFrame !== null) return;
    var self = this;
    this.fitFrame = window.requestAnimationFrame(function () {
      self.fitFrame = null;
      self.measureFit();
    });
  };

  HeroBannerProducts.prototype.onBannerChange = function () {
    var swiper = this.banner && this.banner.swiper;
    var next = swiper ? swiper.realIndex : 0;
    if (next === this.activeGroup) return;
    this.activeGroup = next;
    this.apply(true);
  };

  HeroBannerProducts.prototype.onBreakpoint = function () {
    this.apply(false);
    this.onFitResize();
  };

  HeroBannerProducts.prototype.isDesktop = function () {
    return this.mq.matches;
  };

  /* Show the active group (desktop) or scroll to it (mobile). */
  HeroBannerProducts.prototype.apply = function (animate) {
    var desktop = this.isDesktop();
    var groups = this.groups;
    var active = this.activeGroup;

    this.products.forEach(function (el, i) {
      el.classList.toggle('sg-hero__product--hidden', desktop && groups[i] !== active);
    });

    if (desktop) {
      this.track.scrollLeft = 0;
    } else {
      var first = groups.indexOf(active);
      if (first > -1) this.scrollToIndex(first, animate);
    }

    this.updateArrows();

    // Keep the dimensions established during initial layout. Recalculating
    // the section for every banner transition made the product column move
    // when groups had slightly different title heights.
  };

  HeroBannerProducts.prototype.scrollToIndex = function (index, animate) {
    var target = this.products[index];
    if (!target) return;
    // offsetLeft is measured from the track's border box, while scrollLeft is
    // measured from its content start — without subtracting the left inset,
    // scrolling to the first card would shove it under the padding.
    var style = window.getComputedStyle(this.track);
    var inset = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.borderLeftWidth) || 0);
    var left = target.offsetLeft - this.track.offsetLeft - inset;
    if (left < 0) left = 0;
    if (animate && 'scrollBehavior' in document.documentElement.style) {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }
  };

  /* One card per arrow click. */
  HeroBannerProducts.prototype.step = function (direction) {
    var card = this.products[0];
    if (!card) return;
    var stride = card.offsetWidth + this.gap();
    var left = this.track.scrollLeft + stride * direction;
    if ('scrollBehavior' in document.documentElement.style) {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }
  };

  HeroBannerProducts.prototype.gap = function () {
    var value = parseFloat(getComputedStyle(this.track).columnGap);
    return isNaN(value) ? 0 : value;
  };

  HeroBannerProducts.prototype.onScroll = function () {
    if (this.scrollFrame) return;
    var self = this;
    this.scrollFrame = window.requestAnimationFrame(function () {
      self.scrollFrame = null;
      self.updateArrows();
    });
  };

  HeroBannerProducts.prototype.updateArrows = function () {
    if (!this.prev && !this.next) return;

    var track = this.track;
    // 1px of slack absorbs sub-pixel scroll positions.
    var overflowing = !this.isDesktop() && track.scrollWidth - track.clientWidth > 1;
    var atStart = track.scrollLeft <= 1;
    var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

    if (this.prev) {
      this.prev.classList.toggle('swiper-button-lock', !overflowing);
      this.prev.classList.toggle('swiper-button-disabled', overflowing && atStart);
      this.prev.disabled = !overflowing || atStart;
    }
    if (this.next) {
      this.next.classList.toggle('swiper-button-lock', !overflowing);
      this.next.classList.toggle('swiper-button-disabled', overflowing && atEnd);
      this.next.disabled = !overflowing || atEnd;
    }
  };

  HeroBannerProducts.prototype.destroy = function () {
    this.destroyed = true;
    window.clearTimeout(this.resumeTimer);
    window.cancelAnimationFrame(this.fitFrame);
    window.cancelAnimationFrame(this.fitConfirmFrame);
    window.cancelAnimationFrame(this.scrollFrame);
    window.removeEventListener('resize', this.onFitResize);
    window.removeEventListener('load', this.onFitResize);

    if (this.banner) {
      this.banner.removeEventListener('swiperslidechange', this.onBannerChange);
      this.banner.removeEventListener('swiperinit', this.onBannerChange);
    }
    this.prev?.removeEventListener('click', this.onPrevClick);
    this.next?.removeEventListener('click', this.onNextClick);
    this.track?.removeEventListener('scroll', this.onScroll);

    if (this.productsArea && this.pauseEnabled) {
      this.productsArea.removeEventListener('pointerenter', this.onPointerEnter);
      this.productsArea.removeEventListener('pointerleave', this.onPointerLeave);
      this.productsArea.removeEventListener('pointerdown', this.onProductPointerDown);
      this.productsArea.removeEventListener('click', this.onProductClick);
      this.productsArea.removeEventListener('focusin', this.onPointerEnter);
      this.productsArea.removeEventListener('focusout', this.onPointerLeave);
    }

    if (typeof this.mq.removeEventListener === 'function') {
      this.mq.removeEventListener('change', this.onBreakpoint);
    } else if (typeof this.mq.removeListener === 'function') {
      this.mq.removeListener(this.onBreakpoint);
    }

    this.root.sgHero = null;
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-sg-hero]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.sgHero) return;
      root.sgHero = new HeroBannerProducts(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var roots = [];
    if (event.target.matches && event.target.matches('[data-sg-hero]')) {
      roots.push(event.target);
    }
    event.target.querySelectorAll('[data-sg-hero]').forEach(function (root) {
      roots.push(root);
    });
    Array.prototype.forEach.call(roots, function (root) {
      if (root.sgHero) root.sgHero.destroy();
    });
  });
})();
